import { useState } from 'react';
import { ChevronDown, ChevronUp, TrendingUp, AlertTriangle, BarChart2 } from 'lucide-react';
import { useFunnelInsights } from '../hooks/useApi';

interface FunnelInsightsPanelProps {
  jobId: number;
}

export function FunnelInsightsPanel({ jobId }: FunnelInsightsPanelProps) {
  const [open, setOpen] = useState(true);
  const { data, isLoading } = useFunnelInsights(jobId);

  if (!data && !isLoading) return null;

  const maxBucketCount = data ? Math.max(...data.scoreBuckets.map((b) => b.count), 1) : 1;

  return (
    <div className="bg-stone-50 dark:bg-gray-900 border border-stone-200 dark:border-gray-700 rounded-[10px] mb-4">
      {/* Header */}
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label="Arbie's Insights toggle"
        className="w-full flex items-center justify-between px-4 py-3 text-left"
      >
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-stone-500 dark:text-stone-400 uppercase tracking-wide">
            Arbie's Insights
          </span>
          {data && (
            <span className="text-xs bg-sky-100 dark:bg-sky-900/40 text-sky-700 dark:text-sky-400 px-1.5 py-0.5 rounded font-medium">
              AI
            </span>
          )}
          {data && (
            <span className="text-xs text-stone-400 dark:text-stone-500">
              {data.totalReviewed} of {data.totalCandidates} reviewed
            </span>
          )}
        </div>
        {open ? (
          <ChevronUp size={14} className="text-stone-400" />
        ) : (
          <ChevronDown size={14} className="text-stone-400" />
        )}
      </button>

      {open && (
        <div className="px-4 pb-4 grid grid-cols-1 sm:grid-cols-3 gap-4 border-t border-stone-200 dark:border-gray-700 pt-3">
          {isLoading ? (
            <div className="col-span-3 h-16 bg-stone-100 dark:bg-gray-800 rounded animate-pulse" />
          ) : (
            data && (
              <>
                {/* Top Strengths */}
                <div>
                  <div className="flex items-center gap-1.5 mb-2">
                    <TrendingUp size={12} className="text-emerald-500" />
                    <span className="text-[10px] font-semibold text-emerald-700 dark:text-emerald-400 uppercase tracking-wide">
                      Top Strengths
                    </span>
                  </div>
                  {data.strengths.length === 0 ? (
                    <p className="text-xs text-stone-400 dark:text-stone-500">No reviews yet</p>
                  ) : (
                    <ul className="space-y-1">
                      {data.strengths.map((s, i) => (
                        <li key={i} className="flex items-start gap-1.5">
                          <span className="text-emerald-500 mt-0.5 flex-shrink-0">✓</span>
                          <span className="text-xs text-stone-700 dark:text-stone-200">{s}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Knockout Flags */}
                <div>
                  <div className="flex items-center gap-1.5 mb-2">
                    <AlertTriangle size={12} className="text-amber-500" />
                    <span className="text-[10px] font-semibold text-amber-700 dark:text-amber-400 uppercase tracking-wide">
                      Knockout Flags
                    </span>
                  </div>
                  {data.knockoutFlags.length === 0 ? (
                    <p className="text-xs text-stone-400 dark:text-stone-500">None flagged</p>
                  ) : (
                    <ul className="space-y-1">
                      {data.knockoutFlags.map((f, i) => (
                        <li key={i} className="flex items-start gap-1.5">
                          <span className="text-amber-500 mt-0.5 flex-shrink-0">!</span>
                          <span className="text-xs text-stone-700 dark:text-stone-200">{f}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Score Distribution */}
                <div>
                  <div className="flex items-center gap-1.5 mb-2">
                    <BarChart2 size={12} className="text-sky-500" />
                    <span className="text-[10px] font-semibold text-sky-700 dark:text-sky-400 uppercase tracking-wide">
                      Score Distribution
                    </span>
                  </div>
                  <div className="flex items-end gap-1 h-12">
                    {data.scoreBuckets.map((b) => {
                      const pct = maxBucketCount > 0 ? (b.count / maxBucketCount) * 100 : 0;
                      return (
                        <div key={b.label} className="flex-1 flex flex-col items-center gap-0.5">
                          <span className="text-[9px] text-stone-500 dark:text-stone-400">
                            {b.count > 0 ? b.count : ''}
                          </span>
                          <div
                            className="w-full bg-sky-400 dark:bg-sky-600 rounded-sm min-h-[2px] transition-all"
                            style={{ height: `${Math.max(pct, 4)}%` }}
                          />
                          <span className="text-[9px] text-stone-400 dark:text-stone-500 whitespace-nowrap">
                            {b.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )
          )}
        </div>
      )}
    </div>
  );
}
